// Utilities to parse custom template strings like:
// {{href: '@https://...', alt: 'Whatsapp', width: '24', height: 24}}
// into an HTML <img ... /> string.

import moment from "moment";

function safeJsonParse(possiblyJsonLike) {
  try {
    return JSON.parse(possiblyJsonLike);
  } catch (error) {
    if (
      typeof process !== "undefined" &&
      process.env &&
      process.env.NODE_ENV !== "production"
    ) {
      // Intentionally non-throwing: template parsing should fail soft
      // eslint-disable-next-line no-console
      console.warn("ParserHelper: invalid JSON-like template", error);
    }
    return null;
  }
}

// Reemplaza todas las ocurrencias de {{ ... }} en un string
function processTemplateMatches(text, onMatch) {
  let result = "";
  let i = 0;

  while (i < text.length) {
    const open = text.indexOf("{{", i);
    if (open === -1) {
      result += text.slice(i);
      break;
    }

    result += text.slice(i, open);

    const close = text.indexOf("}}", open + 2);
    if (close === -1) {
      result += text.slice(open);
      break;
    }

    const inner = text.slice(open + 2, close);
    result += onMatch(inner, `{{${inner}}}`);

    i = close + 2;
  }

  return result;
}

function removeTemplates(text) {
  return processTemplateMatches(text, () => "");
}

const TAG_DECL_REGEX =
  /^\s*(htmlImg|htmlLink|htmlImgLink)\s*:\s*([\s\S]{0,10000})$/;

// Helper to build HTML attributes from props
function buildHtmlAttributes(props) {
  return Object.entries(props)
    .filter(([, value]) => value != null)
    .map(([key, value]) => {
      if (typeof value === "boolean") {
        return value ? `${key}="${key}"` : null;
      }
      return `${key}="${String(value)}"`;
    })
    .filter(Boolean)
    .join(" ");
}

function normalizeToJsonObjectString(rawInner) {
  const trimmed = String(rawInner || "").trim();
  if (!trimmed) return null;

  // Split by commas, but be careful about commas inside quoted strings
  const parts = [];
  let current = "";
  let inQuotes = false;
  let quoteChar = "";

  for (const char of trimmed) {
    if (!inQuotes && (char === '"' || char === "'")) {
      inQuotes = true;
      quoteChar = char;
      current += char;
    } else if (inQuotes && char === quoteChar) {
      inQuotes = false;
      quoteChar = "";
      current += char;
    } else if (!inQuotes && char === ",") {
      parts.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  if (current.trim()) {
    parts.push(current.trim());
  }

  // Process each part
  const processedParts = parts.map((part) => {
    const colonIndex = part.indexOf(":");
    if (colonIndex === -1) return part;

    const key = part.substring(0, colonIndex).trim();
    const value = part.substring(colonIndex + 1).trim();

    // Quote the key
    const quotedKey = key.startsWith('"') ? key : `"${key}"`;

    // Handle the value
    let quotedValue = value;
    if (
      !value.startsWith('"') &&
      !value.startsWith("'") &&
      value !== "true" &&
      value !== "false" &&
      value !== "null"
    ) {
      // It's an unquoted string, quote it
      quotedValue = `"${value}"`;
    } else if (value.startsWith("'") && value.endsWith("'")) {
      // Convert single quotes to double quotes
      quotedValue = `"${value
        .slice(1, -1)
        .replaceAll("\\", "\\\\")
        .replaceAll('"', '\\"')}"`;
    }

    return `${quotedKey}: ${quotedValue}`;
  });

  return `{${processedParts.join(", ")}}`;
}

function buildImgHtmlFromProps(props) {
  if (!props || typeof props !== "object") return null;

  const { href, ...rest } = props;
  const attrs = { ...rest };
  if (href != null) attrs.src = href;

  return `<img ${buildHtmlAttributes(attrs)} />`;
}

export function parseImgTemplateToHtml(templateString) {
  if (typeof templateString !== "string") return null;
  const match = removeTemplates(templateString);
  if (!match) return null;

  const inner = match[1];
  const jsonLike = normalizeToJsonObjectString(inner);
  if (!jsonLike) return null;

  const obj = safeJsonParse(jsonLike);
  if (!obj) return null;

  return buildImgHtmlFromProps(obj);
}

// ---- LINK SUPPORT ----

function buildLinkHtmlFromProps(props) {
  if (!props || typeof props !== "object") return null;

  const { text, label, children, title, url, ...rest } = props;

  const attrs = { ...rest };
  if (url != null) attrs.href = url;

  const innerText = text || title || label || children || attrs.href || "";

  return `<a ${buildHtmlAttributes(attrs)}>${innerText}</a>`;
}

// ---- IMAGE WITH LINK SUPPORT ----

function buildImgLinkHtmlFromProps(props) {
  if (!props || typeof props !== "object") return null;

  const {
    href, // image src
    url, // link href
    linkUrl, // alternative link href
    alt,
    title,
    text,
    label,
    children,
    ...rest
  } = props;

  // Build image attributes
  const imgAttrs = { ...rest };
  if (href != null) imgAttrs.src = href;
  if (alt != null) imgAttrs.alt = alt;

  // Build link attributes
  const linkAttrs = {};
  const linkHref = url || linkUrl;
  if (linkHref != null) linkAttrs.href = linkHref;

  const imgHtml = `<img ${buildHtmlAttributes(imgAttrs)} />`;

  if (linkHref) {
    return `<a ${buildHtmlAttributes(linkAttrs)}>${imgHtml}</a>`;
  }

  return imgHtml;
}

export function parseLinkTemplateToHtml(templateString) {
  if (typeof templateString !== "string") return null;
  const match = removeTemplates(templateString);
  if (!match) return null;

  const inner = match[1];
  const jsonLike = normalizeToJsonObjectString(inner);
  if (!jsonLike) return null;

  const obj = safeJsonParse(jsonLike);
  if (!obj) return null;

  return buildLinkHtmlFromProps(obj);
}

export function parseImgLinkTemplateToHtml(templateString) {
  if (typeof templateString !== "string") return null;
  const match = removeTemplates(templateString);
  if (!match) return null;

  const inner = match[1];
  const jsonLike = normalizeToJsonObjectString(inner);
  if (!jsonLike) return null;

  const obj = safeJsonParse(jsonLike);
  if (!obj) return null;

  return buildImgLinkHtmlFromProps(obj);
}

// ---- GENERIC REPLACER SUPPORTING htmlImg / htmlLink / htmlImgLink ----

function detectTagAndProps(inner) {
  const jsonLike = normalizeToJsonObjectString(inner);
  const obj = jsonLike ? safeJsonParse(jsonLike) : null;

  if (!obj) return null;

  // Check for new format: type: htmlImg/htmlLink/htmlImgLink
  if (
    obj.type === "htmlImg" ||
    obj.type === "htmlLink" ||
    obj.type === "htmlImgLink"
  ) {
    const { type, ...props } = obj;
    return { tag: type, props };
  }

  // Check for legacy format: htmlImg: { ... } or htmlLink: { ... }
  const tagMatch = TAG_DECL_REGEX.exec(inner);
  if (tagMatch) {
    const tag = tagMatch[1];
    const rest = tagMatch[2];
    const jsonOuter = normalizeToJsonObjectString(rest);
    const legacyObj = jsonOuter ? safeJsonParse(jsonOuter) : null;
    return { tag, props: legacyObj };
  }

  // Default to img for direct props
  return { tag: "htmlImg", props: obj };
}

export function replaceAllHtmlTemplatesWithHtml(inputText) {
  if (typeof inputText !== "string") return inputText;
  return processTemplateMatches(inputText, (inner, match) => {
    const detected = detectTagAndProps(inner);
    if (!detected?.props) return match;

    if (detected.tag === "htmlLink")
      return buildLinkHtmlFromProps(detected?.props) || match;

    if (detected.tag === "htmlImgLink")
      return buildImgLinkHtmlFromProps(detected?.props) || match;

    return buildImgHtmlFromProps(detected?.props) || match;
  });
}

export function replaceAllImgTemplatesWithHtml(inputText) {
  if (typeof inputText !== "string") return inputText;
  return processTemplateMatches(inputText, (inner, match) => {
    const jsonLike = normalizeToJsonObjectString(inner);
    const obj = jsonLike ? safeJsonParse(jsonLike) : null;
    const html = obj ? buildImgHtmlFromProps(obj) : null;
    return html || match; // fallback to original if parsing fails
  });
}

export function parseDateToLocal(dateString) {
  // Handle invalid inputs
  if (!dateString || typeof dateString !== "string") {
    return "Invalid date";
  }

  const date = moment.utc(dateString);
  if (!date.isValid()) {
    return "Invalid date";
  }

  return date.format("DD/MM/YYYY HH:mm");
}

export default {
  parseImgTemplateToHtml,
  parseLinkTemplateToHtml,
  parseImgLinkTemplateToHtml,
  replaceAllHtmlTemplatesWithHtml,
  replaceAllImgTemplatesWithHtml,
  parseDateToLocal,
};
